import React, { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useCompanion } from "@/companion/CompanionContext";
import { AlertCircle, CheckCircle2, Search } from "lucide-react";
import ReadingModeToggle from "@/components/ReadingModeToggle";
import "@/components/SubpageVessel.css";

/**
 * The shared threshold for every TOTO workspace.
 *
 * Keep operational attention here, at the moment a person arrives at a
 * page—not buried in a page-specific card.  `attention` is intentionally
 * singular: the vessel may summarize a count, but it must never become a
 * second notification center.
 */
export default function Topbar({
  title,
  context,
  attention,
  status,
  primaryAction,
}) {
  const { user } = useAuth();
  const companion = useCompanion();
  const [actionEngaged, setActionEngaged] = useState(false);

  // Subtle boutique underline under the page title so the tint threads through
  // Sidebar → Topbar → active nav. Rainbow leadership stays neutral stone.
  const boutiqueUnderline = {
    PLT: "#BDB2FF", FLL: "#8EC5FF", WYN: "#E6A6CB", PNC: "#E7B780",
    RNO: "#D8C58A", CC: "#D99A9A",
  }[user?.boutique_code] || "#E7E5E4";


  // A primary action is the threshold into revealed work, not a permanently
  // loud slab. Keep the caller's semantics and handlers intact, while giving
  // every title-vessel action the same adaptive physical response.
  const adaptivePrimaryAction = React.isValidElement(primaryAction)
    ? React.cloneElement(primaryAction, {
        className: [primaryAction.props.className, "liquid-glass-action", actionEngaged && "is-liquid-engaged"].filter(Boolean).join(" "),
        "data-liquid-action": "reveal",
        onPointerDown: (event) => {
          primaryAction.props.onPointerDown?.(event);
          if (!event.defaultPrevented) setActionEngaged(true);
        },
        onPointerUp: (event) => {
          primaryAction.props.onPointerUp?.(event);
          setActionEngaged(false);
        },
        onPointerCancel: (event) => {
          primaryAction.props.onPointerCancel?.(event);
          setActionEngaged(false);
        },
        onBlur: (event) => {
          primaryAction.props.onBlur?.(event);
          setActionEngaged(false);
        },
      })
    : primaryAction;

  return (
    <header className="subpage-vessel" data-testid="topbar">
      <div className="subpage-vessel__field" aria-hidden="true" />
      <div className="subpage-vessel__content">
      <div className="subpage-vessel__identity">
        {title && (
          <div className="min-w-0">
            <h1 className="subpage-vessel__title" data-testid="topbar-title">
            {title}
            <span
              aria-hidden="true"
              data-testid="topbar-underline"
              style={{ backgroundColor: boutiqueUnderline }}
              className="subpage-vessel__accent"
            />
            </h1>
            {context && <p className="subpage-vessel__context">{context}</p>}
          </div>
        )}
      </div>
      {(attention || status || primaryAction) && (
        <div className="subpage-vessel__page-state" aria-live="polite">
          {attention && (
            <div className={`subpage-vessel__attention is-${attention.tone || "attention"}`} data-testid="title-vessel-attention">
              <AlertCircle size={14} strokeWidth={1.5} aria-hidden="true" />
              <span>
                <strong>{attention.label}</strong>
                {attention.detail && <small>{attention.detail}</small>}
              </span>
            </div>
          )}
          {status && (
            <div className={`subpage-vessel__status is-${status.tone || "quiet"}`} data-testid="title-vessel-status">
              <CheckCircle2 size={13} strokeWidth={1.5} aria-hidden="true" />
              <span>{status.label}</span>
            </div>
          )}
          {adaptivePrimaryAction && <div className="subpage-vessel__primary-action">{adaptivePrimaryAction}</div>}
        </div>
      )}
      <div className="subpage-vessel__controls">
        <div className="subpage-vessel__search">
          <Search size={14} strokeWidth={1.25} />
          <input
            data-testid="topbar-search"
            placeholder={companion.searchPlaceholder}
            className="subpage-vessel__search-input"
          />
        </div>
        <ReadingModeToggle />
        {user && <span className="subpage-vessel__person" aria-label="Signed-in user">{user.name?.split(" ")[0]}</span>}
      </div>
      </div>
    </header>
  );
}
